import { createSelector } from 'reselect';
import { ApplicationRootState } from 'types';
import { initialState } from './reducer';
import {
  makeSelectShelvesSelected,
  makeSelectShelvesSelectedLastCabinet,
} from 'containers/Configurator/selectors/selection';
import {
  DoorHandleType,
  DrawerHandleType,
  Material,
  MINIMUM_LARGE_DOORS_HEIGHT,
  Position,
  PositionAngle,
  ShelfComposition,
} from 'lib/modelBuilder/constants';
import {
  makeSelectDoorHandles,
  makeSelectDrawersHandles,
} from 'containers/Configurator/selectors/pieces';
import { Color } from 'lib/modelBuilder/entities/Color';
import { DoorHandle } from 'lib/modelBuilder/pieces/DoorHandle';
import { makeSelectIsSameDrawersNumberInShelvesSelected } from 'containers/DrawersPanel/selectors';
import ItemPicker from 'helpers/ItemPicker';

/**
 * Direct selector to the handlersPanel state domain
 */

export const selectHandlesPanelDomain = (state: ApplicationRootState) =>
  state.handlesPanel || initialState;

export const makeSelectIsVisibleHandlesPanel = () =>
  createSelector(selectHandlesPanelDomain, substate => substate.isVisible);

export const makeSelectHandlesSelection = () =>
  createSelector(
    selectHandlesPanelDomain,
    substate => substate.positionsSelected,
  );

export const makeSelectPaintColorHandles = () =>
  createSelector(
    selectHandlesPanelDomain,
    (substate): Color | undefined => substate.color,
  );

export const makeSelectPaintTypeHandles = () =>
  createSelector(
    selectHandlesPanelDomain,
    (substate): DoorHandleType | DrawerHandleType | undefined => substate.type,
  );

export const makeSelectPaintMaterialHandles = () =>
  createSelector(
    selectHandlesPanelDomain,
    (substate): Material | undefined => substate.material,
  );

export const makeSelectGetSetCompositionSelected = () =>
  createSelector(
    makeSelectShelvesSelected(),
    (shelvesSelected): Set<ShelfComposition> => {
      const handleCompositions: Set<ShelfComposition> = new Set();
      for (const shelf of shelvesSelected) {
        if (
          shelf.composition === ShelfComposition.TwoDoors ||
          ShelfComposition.FourDoors
        ) {
          if (shelf.height >= MINIMUM_LARGE_DOORS_HEIGHT) {
            if (shelf.composition === ShelfComposition.TwoDoors)
              handleCompositions.add(ShelfComposition.LargeTwoDoors);
            else handleCompositions.add(ShelfComposition.LargeFourDoors);
          } else {
            handleCompositions.add(shelf.composition);
          }
        } else {
          handleCompositions.add(shelf.composition);
        }
      }
      return handleCompositions;
    },
  );

export const makeSelectDoorHandleColors = () =>
  createSelector(makeSelectDoorHandles(), doorHandles => {
    const colors: Color[] = [];
    for (const item of doorHandles) {
      if (item.color) colors.push(item.color);
    }
    return colors;
  });

export const makeSelectDrawersHandleItems = () =>
  createSelector(makeSelectDrawersHandles(), handles => {
    const items: ItemPicker[] = [];
    for (const item of handles) {
      if (item.color && item.type === DrawerHandleType.Knop)
        items.push(
          new ItemPicker(
            item.color.name,
            `/configurator/assets/images/handles/drawers/handles/${item.color.nameEn
              .replace(' ', '')
              .toLowerCase()}.png`,
            item.color,
          ),
        );
    }
    return items;
  });

export const makeSelectDrawersButtonItems = () =>
  createSelector(makeSelectDrawersHandles(), handles => {
    const items: ItemPicker[] = [];
    for (const item of handles) {
      if (item.color && item.type === DrawerHandleType.Button)
        items.push(
          new ItemPicker(
            item.color.name,
            `/configurator/assets/images/handles/drawers/buttons/${item.color.nameEn
              .replace(' ', '')
              .toLowerCase()}.png`,
            item.color,
          ),
        );
    }
    return items;
  });

export const makeSelectDoorsKnopItems = () =>
  createSelector(makeSelectDoorHandles(), handles => {
    const items: ItemPicker[] = [];
    handles
      .sort((a: DoorHandle, b: DoorHandle) => {
        if (a.color === undefined || b.color === undefined) return 0;
        return b.color.order - a.color.order;
      })
      .reverse();
    for (const item of handles) {
      if (
        item.color &&
        item.type === DoorHandleType.Knop &&
        item.color.id !== 48 /* Filter out brown knop */
      )
        items.push(
          new ItemPicker(
            item.color.name,
            `/configurator/assets/images/handles/doors/${item.color.nameEn
              .replace(' ', '')
              .toLowerCase()}.jpg`,
            item.color,
          ),
        );
    }
    return items;
  });

export const makeSelectHandleDoorSelectedColor = (position: PositionAngle) =>
  createSelector(makeSelectShelvesSelectedLastCabinet(), (shelvesSelected):
    | Color[]
    | null => {
    let color: Color | null = null;
    for (const shelf of shelvesSelected) {
      const door = shelf.getDoor(position);
      if (door && door.handle) {
        if (
          color &&
          ((door.handle as DoorHandle).color as Color).reference !==
            color.reference
        ) {
          return null;
        }
        color = (door.handle as DoorHandle).color as Color;
      }
    }
    if (color) return [color];
    else return null;
  });

export const makeSelectHandleDrawerSelectedColor = (position: Position) =>
  createSelector(
    makeSelectIsSameDrawersNumberInShelvesSelected(),
    makeSelectShelvesSelectedLastCabinet(),
    (sameDrawsers, shelvesSelected): Color | undefined => {
      let color: Color | undefined = undefined;
      if (!sameDrawsers) {
        return undefined;
      }
      for (const shelf of shelvesSelected) {
        for (const drawer of shelf.drawers) {
          if (drawer && drawer.handles) {
            const handle = drawer.handles.find(
              item => item.position === position,
            );
            if (handle) {
              if (color && handle.color.reference !== color.reference) {
                return undefined;
              }
              color = handle.color;
            } else return undefined;
          }
        }
      }
      return color;
    },
  );
